import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import { ProjectDtoIn } from '@/models/in/ProjectDtoIn';
import { ProjectManagementTabType } from '@/models/common/ProjectManagementTabType';
import { formatProjectAmount, getProjectStatusColor, getProjectStatusLabelKey } from '@/helpers/projectStatus';
import DataTable from '@/components/ui/DataTable/DataTable';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import ProjectBlade from '@/pages/Projects/ProjectBlade/ProjectBlade';
import ProjectStatusChangeDialog from './ProjectStatusChangeDialog';
import ProjectNotificationSettingsCard from './ProjectNotificationSettingsCard';

const projectSearch: FilterFn<ProjectDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const searchValue = String(value).toLowerCase();
  const { name, fundId, fundName, projectTypeName, createdByEmail } = row.original;
  return [name, String(fundId), fundName, projectTypeName, createdByEmail].some(
    (field) => field?.toLowerCase().includes(searchValue)
  );
};

/**
 * The admin "אישור פרויקטים" queue. Same shape as the support-requests management screen:
 * status tabs over one DataTable, with the server resolving each tab to its statuses — the
 * "ממתין לאישור" tab covers all three review sub-stages (בדיקה ראשונית / ועדת רווחה /
 * ועדת התמיכות) server-side.
 */
const ProjectsManagementPage = () => {
  const { t, i18n } = useTranslation();

  const [selectedTab, setSelectedTab] = useState<ProjectManagementTabType>(ProjectManagementTabType.PendingApproval);
  const [searchQuery, setSearchQuery] = useState('');
  const [projectToUpdate, setProjectToUpdate] = useState<ProjectDtoIn | null>(null);
  // The list has no room for the coordinator's description, so the office opens the project to
  // read it in full - by the row or by the explicit button (SPR-10302).
  const [projectToView, setProjectToView] = useState<ProjectDtoIn | null>(null);

  const { data: projects, isLoading, refetch } = useGetAuthApi<ProjectDtoIn[]>(
    `management/projects?tab=${selectedTab}`
  );

  const handleCloseStatusDialog = useCallback(() => setProjectToUpdate(null), []);

  const columns: ColumnDef<ProjectDtoIn>[] = [
    {
      id: 'name',
      header: Col(t('projectsManagement.columns.name')),
      accessorKey: 'name',
      enableSorting: true,
      meta: { className: 'min-w-[200px]' },
    },
    {
      // The office works by fund number, and two funds can share a name (SPR-10302).
      id: 'fundId',
      header: Col(t('projectsManagement.columns.fundNumber')),
      accessorKey: 'fundId',
      enableSorting: true,
      meta: { className: 'w-[100px] min-w-[100px] max-w-[100px]' },
    },
    {
      id: 'fundName',
      header: Col(t('projectsManagement.columns.fund')),
      accessorKey: 'fundName',
      enableSorting: true,
      meta: { className: 'min-w-[160px]' },
    },
    {
      id: 'projectTypeName',
      header: Col(t('projectsManagement.columns.projectType')),
      accessorKey: 'projectTypeName',
      enableSorting: true,
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'totalExpensesAmount',
      header: Col(t('projectsManagement.columns.totalAmount')),
      accessorKey: 'totalExpensesAmount',
      enableSorting: true,
      // A bare number, so it cannot go through Cell(cellType.Amount) - that helper
      // destructures the cell value as an object and throws.
      cell: ({ row }) => (
        <span className="text-(--font-tertiary)">
          {formatProjectAmount(row.original.totalExpensesAmount, i18n.language)}
        </span>
      ),
      meta: { className: 'min-w-[130px]' },
    },
    {
      id: 'suppliers',
      header: Col(t('projectsManagement.columns.suppliers')),
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.suppliers.length}</span>,
      meta: { className: 'min-w-[90px] max-w-[90px]' },
    },
    {
      id: 'status',
      header: Col(t('projectsManagement.columns.status')),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div
            className="w-[10px] h-[10px] rounded-full shrink-0"
            style={{ backgroundColor: getProjectStatusColor(row.original.status) }}
          />
          {/* The admin needs the exact sub-stage, not the aggregate label a coordinator sees. */}
          <span>{t(getProjectStatusLabelKey(row.original.status, false))}</span>
        </div>
      ),
      meta: { className: 'min-w-[170px]' },
    },
    {
      id: 'createdByEmail',
      header: Col(t('projectsManagement.columns.createdBy')),
      accessorKey: 'createdByEmail',
      enableSorting: true,
      meta: { className: 'min-w-[180px]' },
    },
    {
      id: 'createdAt',
      header: Col(t('projectsManagement.columns.createdAt')),
      accessorKey: 'createdAt',
      cell: Cell(cellType.Date),
      enableSorting: true,
      meta: { className: 'min-w-[130px] max-w-[130px]' },
    },
    {
      id: 'actions',
      header: Col(''),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-2 whitespace-nowrap">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              setProjectToView(row.original);
            }}
          >
            {t('projectsManagement.viewProject')}
          </Button>
          {row.original.allowedNextStatuses.length > 0 && (
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                setProjectToUpdate(row.original);
              }}
            >
              {t('projectsManagement.statusDialog.submit')}
            </Button>
          )}
        </div>
      ),
      // Fixed, not flexible: as a flexible column the table squeezed it below the two buttons
      // and cut their labels ("הצגת מיז", "עדכון סטטו") on an ordinary laptop screen.
      meta: { className: 'w-[250px] min-w-[250px] max-w-[250px]', lockLayout: true },
    },
  ];

  return (
    <div>
      <ProjectNotificationSettingsCard />

      {/* Radix Tabs are string-valued, so the numeric tab enum round-trips through String()/Number()
          - the same conversion SupportRequestsManagementPage does. */}
      <Tabs
        value={String(selectedTab)}
        onValueChange={(value) => setSelectedTab(Number(value) as ProjectManagementTabType)}
        style={{ direction: 'rtl' }}
        className="mb-6"
      >
        <TabsList>
          <TabsTrigger value={String(ProjectManagementTabType.PendingApproval)}>{t('projectsManagement.tabs.pending')}</TabsTrigger>
          <TabsTrigger value={String(ProjectManagementTabType.Approved)}>{t('projectsManagement.tabs.approved')}</TabsTrigger>
          <TabsTrigger value={String(ProjectManagementTabType.Rejected)}>{t('projectsManagement.tabs.rejected')}</TabsTrigger>
        </TabsList>
      </Tabs>

      <DataTable
        title={t('projectsManagement.title')}
        data={projects ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex items-center justify-end gap-3">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="projectsManagement.search"
            />
          </div>
        }
        emptyStateContent={{ title: t('projectsManagement.noData'), description: t('projectsManagement.noDataDescription') }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={projectSearch}
        handleRowClick={setProjectToView}
        activeRecord={projectToView ?? undefined}
      />

      {projectToView && (
        <ProjectBlade
          project={projectToView}
          onClose={() => setProjectToView(null)}
          exactStatus
          defaultTab="details"
        />
      )}

      {projectToUpdate && (
        <ProjectStatusChangeDialog
          open
          onClose={handleCloseStatusDialog}
          project={projectToUpdate}
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default ProjectsManagementPage;
